import { Divider, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import Link from "next/link";

import { CustomElement } from "@/components/ui/box/CustomElement";
import { LoadMoreBtnForList } from "@/components/ui/buttons/LoadMoreBtnForList";
import DefaultImage from "@/components/ui/DefaultImage";
import { NoResultsComponent } from "@/components/ui/NoResultsComponent";
import { Spinner } from "@/components/ui/Spinner";
import { useRenderHook } from "@/hooks/useRenderHook";
import { h_400_14_135_28, h_700_21_normal_42 } from "@/styles/fontStyles";
import { addressSelector } from "@/utils/addressSelector";
import { fundMask } from "@/utils/cardMask";
import { formatNumberFull } from "@/utils/formatNumber";
import { imgSelector } from "@/utils/imgSelector";


export const FundsCards = () => {
  const {
    listForRender,
    isResults,
    isVisibleSeeMore,
    isLoading,
    handleDetailsClick,
    handlerLoadMore,
  } = useRenderHook();

  const fundLinkSelector = (typeRecord, slug) => {
    return typeRecord === "Venture Capital"
      ? "/investors/funds/" + slug
      : "/investors/firms/" + slug;
  };

  return (
    <Flex
      w={"100%"}
      h={!isResults ? "calc(100vh - 320px)" : "fit-content"}
      flexDirection={"column"}
    >
      {(isLoading || listForRender === null) && (
        <Flex
          w={"full"}
          h={"100%"}
          justify={"center"}
          align={"center"}
          py={"30px"}
        >
          <Spinner />
        </Flex>
      )}
      {listForRender?.length === 0 && <NoResultsComponent search={true} />}
      {listForRender && listForRender?.length > 0 && (
        <SimpleGrid
          columns={{ base: 1, md: 2, desk: 3 }}
          spacing={"20px"}
          w={"100%"}
        >
          {listForRender?.map((item, idx) => {
            return (
              <FundCard
                key={`fundCard${item?.id}${idx}`}
                data={item}
                mask={fundMask}
                handlerDetails={() => handleDetailsClick(item)}
                links={{
                  href: fundLinkSelector(item?.typeRecord, item.slug),
                  alt: item?.nameBrand || "",
                }}
              />
            );
          }) ?? null}
        </SimpleGrid>
      )}
      <LoadMoreBtnForList
        {...{ isVisibleSeeMore, isResults, isLoading, handlerLoadMore }}
      />
    </Flex>
  );
};

function FundCard({ data, handlerDetails, handlerTag, links, i }) {
  const address = addressSelector({ data: data?.addresses });

  return (
    <CustomElement
      variant={"whiteBtn"}
      customStyle={{
        boxShadow: "0px 0px 14px 0px rgba(114, 114, 114, 0.25)",
        p: "20px",
        w: "100%",
        h: "fit-content",
        borderRadius: "10px",
        cursor: "default",
      }}
    >
      <Flex
        flexDir={"column"}
        gap={"16px"}
        w={"100%"}
        justify={"left"}
        id={`company_${i}`} // !!! important for open cta modal on scroll
      >
        <Flex justify={"space-between"} align={"center"}>
          <Link
            href={links?.href || "/investors"}
            alt={links?.alt}
            onClick={handlerDetails}
          >
            <Flex
              w={"80px"}
              minW={"80px"}
              minH={"80px"}
              maxW={"80px"}
              maxH={"80px"}
              justify={"center"}
              align={"center"}
              borderRadius={"8px"}
              border={"4px solid"}
              borderColor={"white"}
              bgColor={"white"}
              boxShadow={"0px 0px 14px 0px rgba(114, 114, 114, 0.25)"}
              overflow={"hidden"}
            >
              <DefaultImage
                src={imgSelector({ data: data })}
                options={{
                  objectFit: "scale-down",
                  alt: data?.nameBrand || "Funds or Investment Firms",
                  w: "80px",
                  h: "80px",
                }}
              />
            </Flex>
          </Link>
          <Flex gap={"8px"} flexWrap={"wrap"} justify={"right"}>
            {data?.tags?.slice(0, 4)?.map((el, index) => {
              return (
                <Tags
                  key={"tags" + index + el?.id}
                  {...{ el, handler: handlerTag }}
                />
              );
            })}
          </Flex>
        </Flex>
        <Divider borderColor={"grey"} />
        <Text
          className="truncate2strings"
          whiteSpace={"pre-wrap"}
          textAlign={"left"}
          minH={"40px"}
          pt={"2px"}
          {...h_700_21_normal_42}
        >
          {data?.nameBrand}
        </Text>
        <Flex
          flexDir={"column"}
          gap={"16px"}
          textAlign={"left"}
          {...h_400_14_135_28}
          whiteSpace={"pre-wrap"}
        >
          {data?.activeRound?.stage ? (
            <Text className="truncateString">{`Stage: ${data?.activeRound?.stage}`}</Text>
          ) : (
            <Flex w={"1px"} h={"19px"}></Flex>
          )}
          {data?.dateYearEstablished ? (
            <Text>{`Year Founded: ${data?.dateYearEstablished}`}</Text>
          ) : (
            <Flex w={"1px"} h={"19px"}></Flex>
          )}
          {data?.text?.descriptionEdited ? (
            <Text className="truncate2strings" h={"40px"}>
              {data?.text?.descriptionEdited}
            </Text>
          ) : (
            <Flex w={"1px"} h={"40px"}></Flex>
          )}
        </Flex>
        <Flex
          flexDir={"column"}
          gap={"16px"}
          {...h_400_14_135_28}
          color={"black"}
        >
          <Flex
            {...{
              p: "14px 10px 10px 10px",
              bgColor: "menuGray",
              justify: "space-between",
              borderRadius: "4px",
              gap: "8px",
            }}
          >
            <Text>{`HQ`}</Text>
            <Text {...{ maxW: "230px" }} isTruncated>
              {address || "—"}
            </Text>
          </Flex>
          <Flex
            {...{
              p: "14px 10px 10px 10px",
              bgColor: "menuGray",
              justify: "space-between",
              borderRadius: "4px",
              gap: "8px",
            }}
          >
            <Text>{`AUM`}</Text>
            <Text {...{ maxW: "230px" }} isTruncated>
              {formatNumberFull(data?.totalRaised) || "—"}
            </Text>
          </Flex>
        </Flex>
        <Link
          href={links?.href || "/investors"}
          alt={links?.alt}
          style={{ width: "fit-content" }}
        >
          <CustomElement
            variant={"blueBtn"}
            title={"View Details"}
            customStyle={{ maxW: "141px", w: "141px" }}
          />
        </Link>
      </Flex>
    </CustomElement>
  );
}
//test
function Tags({ el }) {
  return (
    <Flex
      maxW={"100px"}
      p={"6px 10px 2px 10px"}
      bgColor={el?.colors?.color || "menuGray"}
      borderRadius={"4px"}
      maxH={"30px"}
      {...selectedTagStyle(el)}
    >
      <Text
        className="truncateString"
        textAlign={"center"}
        w={"100%"}
        color={"blackColor"}
        {...h_400_14_135_28}

        // handler={() => handler(el)}  (from props)
      >
        {el?.name || "Fund"}
      </Text>
    </Flex>
  );
}

function selectedTagStyle(tag) {
  let tagStyle = {};
  switch (tag) {
    case "Fund":
    case "":
      tagStyle = {
        bgColor: "mainBlue",
        color: "white",
        minH: "30px",
        maxW: "fit-content",
      };
      break;
    case "Asset Manager":
      tagStyle = {
        bgColor: "blue",
        color: "white",
        maxW: "fit-content",
      };
      break;
    default:
      tagStyle = {
        // bgColor: 'menuSearchColor',
        color: "blackColor",
      };
      break;
  }
  return tagStyle;
}
